<template>
  <div class="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
    <div class="flex justify-between items-center mb-6">
      <div>
        <h1 class="text-3xl font-bold text-gray-900 dark:text-gray-100">营销素材</h1>
        <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">
          每个「素材位」是 App 里一个固定位置。内容是一整张图，改文案 = 换图上传，不用发版。停用后立即不再下发。
        </p>
      </div>
      <button class="primary-btn" @click="openCreate">新建活动</button>
    </div>

    <div class="flex gap-2 mb-4">
      <button v-for="option in slotFilters" :key="option.value"
        class="px-4 py-1.5 rounded-full text-sm border transition"
        :class="activeSlot === option.value
          ? 'bg-indigo-600 text-white border-indigo-600'
          : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-700'"
        @click="activeSlot = option.value">
        {{ option.label }}（{{ countBySlot(option.value) }}）
      </button>
    </div>

    <div v-if="loading" class="flex justify-center items-center py-20">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
    </div>

    <div v-else-if="filteredCampaigns.length === 0"
      class="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-10 text-center text-gray-500 dark:text-gray-400">
      还没有活动。新建一条并启用，App 下次冷启动就会弹。
    </div>

    <div v-else class="space-y-4">
      <div v-for="campaign in filteredCampaigns" :key="campaign.id"
        class="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 flex gap-4 items-center">
        <img :src="campaign.imageUrl" alt=""
          class="w-24 h-[88px] object-contain rounded-md bg-gray-100 dark:bg-gray-700 shrink-0" />

        <div class="flex-1 min-w-0">
          <div class="flex items-center gap-2 mb-1">
            <span class="font-semibold text-gray-900 dark:text-gray-100">{{ campaign.name }}</span>
            <span class="text-xs px-1.5 py-0.5 rounded"
              :class="campaign.enabled
                ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'">
              {{ campaign.enabled ? '启用中' : '已停用' }}
            </span>
            <span v-if="campaign.containsPayment"
              class="text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300">
              含购买（iOS 不投放）
            </span>
          </div>
          <div class="text-xs text-gray-500 dark:text-gray-400 space-y-0.5">
            <div>素材位：{{ PromoSlotLabel[campaign.slot] }} · 顺序：{{ campaign.sortOrder }} · 平台：{{ PromoPlatformLabel[campaign.platform] }}</div>
            <div>标识：{{ campaign.code }} · 优先级：{{ campaign.priority }}</div>
            <div>
              跳转：{{ PromoLinkTypeLabel[campaign.linkType] }}
              <span v-if="campaign.linkValue"> → {{ campaign.linkValue }}</span>
            </div>
            <div>时间窗：{{ formatWindow(campaign.startAt, campaign.endAt) }}</div>
          </div>
        </div>

        <div class="flex items-center gap-2 shrink-0">
          <button class="secondary-btn" @click="openPreview(campaign)">预览</button>
          <button class="secondary-btn" @click="openEdit(campaign)">编辑</button>
          <button class="secondary-btn" @click="toggleEnabled(campaign)">
            {{ campaign.enabled ? '停用' : '启用' }}
          </button>
          <button class="secondary-btn" @click="duplicate(campaign)">复制</button>
        </div>
      </div>
    </div>

    <!-- 编辑抽屉 -->
    <div v-if="editorOpen" class="fixed inset-0 z-40 flex justify-end bg-black/40" @click.self="closeEditor">
      <div class="w-[560px] h-full bg-white dark:bg-gray-800 overflow-y-auto p-6">
        <div class="flex justify-between items-center mb-4">
          <h2 class="text-xl font-bold text-gray-900 dark:text-gray-100">
            {{ editingId ? '编辑活动' : '新建活动' }}
          </h2>
          <button class="text-gray-400 hover:text-gray-600" @click="closeEditor">关闭</button>
        </div>

        <div class="space-y-4">
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">素材位</label>
              <select v-model="form.slot" class="form-input w-full">
                <option v-for="option in slotFilters.slice(1)" :key="option.value" :value="option.value">
                  {{ option.label }}
                </option>
              </select>
            </div>
            <div>
              <label class="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">顺序（小的在前）</label>
              <input v-model.number="form.sortOrder" type="number" class="form-input w-full" />
            </div>
          </div>

          <div>
            <label class="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">活动名称</label>
            <input v-model="form.name" type="text" class="form-input w-full" placeholder="例如 剑桥雅思21 首发" />
          </div>

          <div v-if="!editingId">
            <label class="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">活动标识</label>
            <input v-model="form.code" type="text" class="form-input w-full" placeholder="英文/数字，例如 ielts21" />
          </div>

          <div>
            <label class="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
              整图（{{ PromoSlotImageSpec[form.slot] }}）
            </label>
            <input type="file" accept="image/png,image/jpeg" class="form-input w-full" @change="handleUpload" />
            <img v-if="form.imageUrl" :src="form.imageUrl" alt=""
              class="mt-2 w-[316px] rounded-md bg-gray-100 dark:bg-gray-700" />
            <p v-if="uploading" class="text-xs text-gray-500 mt-1">上传中…</p>
          </div>

          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">跳转类型</label>
              <select v-model="form.linkType" class="form-input w-full">
                <option value="none">不可点击</option>
                <option value="app">App 内页面</option>
                <option value="web">外部网页</option>
              </select>
            </div>
            <div>
              <label class="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">投放平台</label>
              <select v-model="form.platform" class="form-input w-full">
                <option value="all">全平台</option>
                <option value="ios">仅 iOS</option>
                <option value="android">仅 Android</option>
              </select>
            </div>
          </div>

          <div v-if="form.linkType !== 'none'">
            <label class="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">跳转目标</label>
            <input v-model="form.linkValue" type="text" class="form-input w-full"
              :placeholder="form.linkType === 'app' ? '/pricing' : 'https://…'" />
            <div v-if="form.linkType === 'app'" class="flex flex-wrap gap-2 mt-2">
              <button v-for="preset in PromoLinkPresets" :key="preset.value" type="button"
                class="text-xs px-2 py-1 rounded border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300"
                @click="form.linkValue = preset.value">
                {{ preset.label }}
              </button>
            </div>
          </div>

          <label class="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            <input v-model="form.containsPayment" type="checkbox" />
            跳转目标涉及购买（勾选后 iOS 端不投放，规避 App Store 3.1.1）
          </label>

          <div class="grid grid-cols-3 gap-3">
            <div>
              <label class="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">优先级</label>
              <input v-model.number="form.priority" type="number" class="form-input w-full" />
            </div>
            <div>
              <label class="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">最低版本号</label>
              <input v-model.number="form.minVersionCode" type="number" class="form-input w-full" placeholder="不限" />
            </div>
            <div class="flex items-end pb-2">
              <label class="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <input v-model="form.enabled" type="checkbox" />
                启用
              </label>
            </div>
          </div>

          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">开始时间</label>
              <input v-model="form.startAtLocal" type="datetime-local" class="form-input w-full" />
            </div>
            <div>
              <label class="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">结束时间</label>
              <input v-model="form.endAtLocal" type="datetime-local" class="form-input w-full" />
            </div>
          </div>
          <p class="text-xs text-gray-500 dark:text-gray-400">时间按本地时区填写，提交时转成 UTC。</p>
        </div>

        <div class="flex gap-3 mt-6">
          <button class="primary-btn flex-1" :disabled="saving" @click="save">
            {{ saving ? '保存中…' : '保存' }}
          </button>
          <button class="secondary-btn" @click="closeEditor">取消</button>
        </div>
      </div>
    </div>

    <!-- 预览：按真机弹窗比例渲染，确认关闭按钮位置与整图观感 -->
    <div v-if="previewCampaign" class="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
      @click.self="previewCampaign = null">
      <div class="bg-gray-100 dark:bg-gray-800 rounded-2xl p-6">
        <div class="relative" :style="{ width: previewWidth + 'px' }">
          <img :src="previewCampaign.imageUrl" alt="" :style="{ width: previewWidth + 'px' }" class="block" />
          <div v-if="previewCampaign.slot === 'promo_modal'"
            class="absolute -bottom-12 left-0 right-0 flex justify-center">
            <div class="w-[42px] h-[42px] rounded-full border-2 border-white/95 flex items-center justify-center">
              <span class="text-white text-xl leading-none">×</span>
            </div>
          </div>
        </div>
        <div class="text-center text-xs text-gray-500 dark:text-gray-400"
          :class="previewCampaign.slot === 'promo_modal' ? 'mt-16' : 'mt-4'">
          <template v-if="previewCampaign.slot === 'promo_modal'">
            真机效果：黑色遮罩 + 整图 + 下方白色圆圈关闭按钮
          </template>
          <template v-else>
            真机效果：按卡片宽度铺满，左右滑动查看，底部为指示点
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue'
import toast from '@/utils/toaster'
import {
  createPromoCampaignAPI,
  getPromoCampaignsAPI,
  updatePromoCampaignAPI,
  uploadPromoImageAPI,
} from '@/api/promo'
import {
  PromoLinkPresets,
  PromoLinkTypeLabel,
  PromoPlatformLabel,
  PromoSlotImageSpec,
  PromoSlotLabel,
  type PromoCampaign,
  type PromoLinkType,
  type PromoPlatform,
  type PromoSlot,
} from '@/types/promo'

const campaigns = ref<PromoCampaign[]>([])
const loading = ref(false)
const saving = ref(false)
const uploading = ref(false)
const editorOpen = ref(false)
const editingId = ref<number | null>(null)
const previewCampaign = ref<PromoCampaign | null>(null)
/** 'all' = 不过滤 */
const activeSlot = ref<PromoSlot | 'all'>('all')

const slotFilters = [
  { value: 'all' as const, label: '全部' },
  { value: 'promo_modal' as const, label: PromoSlotLabel.promo_modal },
  { value: 'home_carousel' as const, label: PromoSlotLabel.home_carousel },
  { value: 'home_banner' as const, label: PromoSlotLabel.home_banner },
]

const filteredCampaigns = computed(() =>
  activeSlot.value === 'all'
    ? campaigns.value
    : campaigns.value.filter((c) => c.slot === activeSlot.value)
)

function countBySlot(slot: PromoSlot | 'all'): number {
  return slot === 'all'
    ? campaigns.value.length
    : campaigns.value.filter((c) => c.slot === slot).length
}

const previewWidth = computed(() =>
  previewCampaign.value?.slot === 'promo_modal' ? 316 : 320
)

const form = reactive({
  code: '',
  name: '',
  slot: 'promo_modal' as PromoSlot,
  sortOrder: 0,
  imageUrl: '',
  linkType: 'none' as PromoLinkType,
  linkValue: '',
  containsPayment: false,
  platform: 'all' as PromoPlatform,
  minVersionCode: null as number | null,
  priority: 0,
  enabled: false,
  startAtLocal: '',
  endAtLocal: '',
})

function formatWindow(startAt: string | null, endAt: string | null): string {
  if (!startAt && !endAt) return '长期有效'
  const fmt = (v: string) => new Date(v).toLocaleString('zh-CN', { hour12: false })
  return `${startAt ? fmt(startAt) : '立即'} ~ ${endAt ? fmt(endAt) : '长期'}`
}

function toIsoOrNull(local: string): string | null {
  if (!local) return null
  return new Date(local).toISOString()
}

function toLocalInput(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

async function load() {
  loading.value = true
  try {
    const res = await getPromoCampaignsAPI()
    campaigns.value = res.data?.campaigns ?? []
  } catch {
    toast.error('加载营销活动失败')
  } finally {
    loading.value = false
  }
}

function resetForm() {
  form.code = ''
  form.name = ''
  form.slot = 'promo_modal'
  form.sortOrder = 0
  form.imageUrl = ''
  form.linkType = 'none'
  form.linkValue = ''
  form.containsPayment = false
  form.platform = 'all'
  form.minVersionCode = null
  form.priority = 0
  form.enabled = false
  form.startAtLocal = ''
  form.endAtLocal = ''
}

function openCreate() {
  editingId.value = null
  resetForm()
  editorOpen.value = true
}

function openEdit(campaign: PromoCampaign) {
  editingId.value = campaign.id
  form.code = campaign.code
  form.name = campaign.name
  form.slot = campaign.slot
  form.sortOrder = campaign.sortOrder
  form.imageUrl = campaign.imageUrl
  form.linkType = campaign.linkType
  form.linkValue = campaign.linkValue ?? ''
  form.containsPayment = campaign.containsPayment
  form.platform = campaign.platform
  form.minVersionCode = campaign.minVersionCode
  form.priority = campaign.priority
  form.enabled = campaign.enabled
  form.startAtLocal = toLocalInput(campaign.startAt)
  form.endAtLocal = toLocalInput(campaign.endAt)
  editorOpen.value = true
}

function closeEditor() {
  editorOpen.value = false
}

function openPreview(campaign: PromoCampaign) {
  previewCampaign.value = campaign
}

async function handleUpload(event: Event) {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  if (!file) return
  uploading.value = true
  try {
    const res = await uploadPromoImageAPI(file)
    if (!res.data?.imageUrl) {
      toast.error('上传失败')
      return
    }
    form.imageUrl = res.data.imageUrl
    toast.success('图片已上传')
  } catch {
    toast.error('上传失败')
  } finally {
    uploading.value = false
    input.value = ''
  }
}

async function save() {
  if (!form.name.trim()) {
    toast.error('请填活动名称')
    return
  }
  if (!form.imageUrl) {
    toast.error('请先上传整图')
    return
  }
  if (!editingId.value && !form.code.trim()) {
    toast.error('请填活动标识')
    return
  }
  if (form.linkType !== 'none' && !form.linkValue.trim()) {
    toast.error('请填跳转目标')
    return
  }

  saving.value = true
  try {
    const payload = {
      name: form.name.trim(),
      slot: form.slot,
      sortOrder: form.sortOrder,
      imageUrl: form.imageUrl,
      linkType: form.linkType,
      linkValue: form.linkType === 'none' ? null : form.linkValue.trim(),
      containsPayment: form.containsPayment,
      platform: form.platform,
      minVersionCode: form.minVersionCode ?? null,
      startAt: toIsoOrNull(form.startAtLocal),
      endAt: toIsoOrNull(form.endAtLocal),
      priority: form.priority,
      enabled: form.enabled,
    }

    if (editingId.value) {
      await updatePromoCampaignAPI(editingId.value, payload)
      toast.success('已保存')
    } else {
      await createPromoCampaignAPI({ ...payload, code: form.code.trim() })
      toast.success('已创建')
    }
    editorOpen.value = false
    await load()
  } catch {
    toast.error('保存失败')
  } finally {
    saving.value = false
  }
}

async function toggleEnabled(campaign: PromoCampaign) {
  try {
    await updatePromoCampaignAPI(campaign.id, { enabled: !campaign.enabled })
    toast.success(campaign.enabled ? '已停用' : '已启用')
    await load()
  } catch {
    toast.error('操作失败')
  }
}

/** 复制一条：换图/改文案时从旧活动复制一份，旧的不删只停用，需要回滚就重新启用 */
function duplicate(campaign: PromoCampaign) {
  editingId.value = null
  form.code = `${campaign.code}_copy`
  form.name = `${campaign.name} 副本`
  form.slot = campaign.slot
  form.sortOrder = campaign.sortOrder
  form.imageUrl = campaign.imageUrl
  form.linkType = campaign.linkType
  form.linkValue = campaign.linkValue ?? ''
  form.containsPayment = campaign.containsPayment
  form.platform = campaign.platform
  form.minVersionCode = campaign.minVersionCode
  form.priority = campaign.priority
  form.enabled = false
  form.startAtLocal = toLocalInput(campaign.startAt)
  form.endAtLocal = toLocalInput(campaign.endAt)
  editorOpen.value = true
}

onMounted(load)
</script>
